import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  Target, 
  TrendingUp, 
  Users, 
  Award,
  Briefcase,
  Sparkles,
  RefreshCw,
  Shield,
  Cloud,
  Network,
  Brain,
  Monitor,
  Star,
  KanbanSquare
} from "lucide-react";
import { toast } from "sonner";
import LeadDetailSheet from "@/components/LeadDetailSheet";

export default function Dashboard() {
  const { theme, branding } = useTheme();
  const [stats, setStats] = useState(null);
  const [bestBets, setBestBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, betsRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/leads/best-bets?limit=5')
      ]);
      setStats(statsRes.data);
      setBestBets(betsRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getScoreColor = (score) => {
    if (score >= 80) return "text-emerald-400";
    if (score >= 50) return "text-theme-secondary";
    return "text-theme-danger";
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return "Excellent";
    if (score >= 50) return "Good";
    return "Low";
  };

  const statCards = [
    { title: "Total Leads", value: stats?.total_leads || 0, icon: Briefcase, color: "from-[#2a9d8f] to-[#1a6b62]", iconColor: "text-theme-primary-light" },
    { title: "New Today", value: stats?.new_leads || 0, icon: Sparkles, color: "from-[#e9c46a] to-[#d4a84b]", iconColor: "text-theme-dark" },
    { title: "In Progress", value: stats?.in_progress || 0, icon: TrendingUp, color: "from-[#f4a261] to-[#e76f51]", iconColor: "text-white" },
    { title: "Interviews", value: stats?.interviews || 0, icon: Users, color: "from-[#2a9d8f] to-[#264653]", iconColor: "text-theme-secondary" },
    { title: "Offers", value: stats?.offers || 0, icon: Award, color: "from-[#40c9ba] to-[#2a9d8f]", iconColor: "text-theme-dark" },
    { title: "Avg Score", value: stats?.avg_score || 0, icon: Target, color: "from-[#e9c46a] to-[#f4a261]", iconColor: "text-theme-dark", suffix: "" },
  ];

  const expertiseAreas = [
    { icon: Cloud, label: "AWS", color: "text-theme-accent" },
    { icon: Shield, label: "DevSecOps", color: "text-theme-primary" },
    { icon: Brain, label: "AI/ML", color: "text-theme-secondary" },
    { icon: Network, label: "PrivateLink", color: "text-theme-primary-light" },
    { icon: Shield, label: "GovCloud", color: "text-theme-danger" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin mx-auto mb-4" style={{color: 'var(--primary-color)'}} />
          <p className="font-mono" style={{color: 'var(--accent-color)'}}>LOADING DATA...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Hero Section - Retro Monitor Style */}
      <div className="relative overflow-hidden rounded-xl sm:rounded-2xl retro-texture" style={{
        background: `linear-gradient(145deg, var(--secondary-color) 0%, var(--bg-dark) 50%, var(--secondary-color) 100%)`,
        border: '3px solid var(--accent-color)',
        boxShadow: '4px 4px 0 rgba(139, 69, 19, 0.3), inset 0 0 60px rgba(42, 157, 143, 0.1)'
      }}>
        {/* CRT Scanlines Effect */}
        <div className="absolute inset-0 pointer-events-none opacity-10" style={{
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)'
        }} />
        
        {/* Glow Effects */}
        <div className="absolute -top-20 -right-20 w-40 sm:w-80 h-40 sm:h-80 rounded-full blur-3xl" style={{backgroundColor: 'var(--primary-color)', opacity: 0.2}} />
        <div className="absolute -bottom-20 -left-20 w-30 sm:w-60 h-30 sm:h-60 rounded-full blur-3xl" style={{backgroundColor: 'var(--accent-color)', opacity: 0.15}} />
        
        <div className="relative p-4 sm:p-6 md:p-10">
          <div className="flex flex-col gap-4 sm:gap-6">
            {/* Header Row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 sm:gap-5">
                {/* Logo */}
                <img 
                  src={branding.logo_url || "https://customer-assets.emergentagent.com/job_gov-deal-hunt/artifacts/qbft4dvs_hero.png"}
                  alt={branding.company_name || "Westbay Logo"}
                  className="w-14 h-14 sm:w-20 md:w-24 sm:h-20 md:h-24 rounded-lg sm:rounded-xl object-cover border-2"
                  style={{borderColor: 'var(--accent-color)', opacity: 0.6, boxShadow: '3px 3px 0 rgba(139, 69, 19, 0.5)'}}
                />
                <div>
                  <h1 className="font-spraypaint text-2xl sm:text-4xl md:text-5xl" style={{color: 'var(--accent-color)', textShadow: '3px 3px 6px rgba(0,0,0,0.5)'}}>
                    {branding.company_name || "Westbay"}
                  </h1>
                  <p className="font-mono text-xs sm:text-sm tracking-[0.15em] sm:tracking-[0.3em]" style={{color: 'var(--primary-color)'}}>
                    {branding.tagline || "CONTRACT CENTER"}
                  </p>
                </div>
              </div>
              
              {/* Stats Display - Desktop */}
              <div className="hidden sm:flex flex-col items-end gap-2">
                <div className="p-3 sm:p-4 rounded-lg sm:rounded-xl border-2" style={{backgroundColor: 'var(--bg-dark)', borderColor: 'var(--accent-color)', opacity: 0.4, boxShadow: '3px 3px 0 rgba(233, 196, 106, 0.2)'}}>
                  <p className="text-3xl sm:text-5xl font-bold font-mono" style={{color: 'var(--accent-color)', textShadow: '0 0 20px rgba(233, 196, 106, 0.4)'}}>
                    {stats?.total_leads || 0}
                  </p>
                  <p className="text-xs sm:text-sm font-mono" style={{color: 'var(--text-muted)'}}>ACTIVE LEADS</p>
                </div>
              </div>
            </div>
            
            {/* Description - Hidden on smallest screens */}
            <p className="hidden sm:block text-sm sm:text-base md:text-lg max-w-xl leading-relaxed" style={{color: 'var(--text-muted)'}}>
              {branding.description || "AI-powered contract hunting for elite cloud & security professionals."}
            </p>
            
            {/* Expertise Tags - Scrollable on mobile */}
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-none">
              {expertiseAreas.map((area) => (
                <div 
                  key={area.label}
                  className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border flex-shrink-0"
                  style={{backgroundColor: 'var(--bg-dark)', opacity: 0.8, borderColor: 'var(--primary-color)', opacity: 0.4}}
                >
                  <area.icon className={`w-3 h-3 sm:w-4 sm:h-4 ${area.color}`} />
                  <span className="text-xs font-bold whitespace-nowrap" style={{color: 'var(--text-light)'}}>{area.label}</span>
                </div>
              ))}
            </div>
            
            {/* Mobile Stats + Refresh */}
            <div className="flex items-center justify-between sm:justify-end gap-3">
              {/* Mobile stats */}
              <div className="sm:hidden p-2.5 rounded-lg bg-theme-darker border border-theme-secondary/40">
                <p className="text-2xl font-bold font-mono text-theme-secondary">{stats?.total_leads || 0}</p>
                <p className="text-theme-muted text-xs font-mono">LEADS</p>
              </div>
              <Button 
                onClick={fetchData} 
                className="bg-gradient-to-r from-[#2a9d8f] to-[#1a6b62] hover:from-[#40c9ba] hover:to-[#2a9d8f] text-theme-light border-2 border-[#40c9ba]/50 font-bold text-xs sm:text-sm"
                style={{boxShadow: '2px 2px 0 rgba(42, 157, 143, 0.4)'}}
                data-testid="refresh-dashboard"
              >
                <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                REFRESH
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-4" data-testid="stats-grid">
        {statCards.map((stat, index) => (
          <div 
            key={stat.title} 
            className="p-2 sm:p-4 rounded-lg sm:rounded-xl border transition-all cursor-pointer group"
            style={{
              backgroundColor: 'var(--bg-dark)', 
              opacity: 0.8, 
              borderColor: 'var(--primary-color)', 
              boxShadow: '2px 2px 0 rgba(38, 70, 83, 0.5)'
            }}
            data-testid={`stat-${stat.title.toLowerCase().replace(' ', '-')}`}
          >
            <div className={`w-7 h-7 sm:w-10 sm:h-10 rounded-md sm:rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center mb-2 sm:mb-3 group-hover:scale-110 transition-transform`} style={{boxShadow: '1px 1px 0 rgba(0,0,0,0.2)'}}>
              <stat.icon className={`w-3.5 h-3.5 sm:w-5 sm:h-5 ${stat.iconColor}`} />
            </div>
            <p className="text-lg sm:text-2xl font-bold font-mono" style={{color: 'var(--text-light)'}}>
              {stat.value}{stat.suffix || ""}
            </p>
            <p className="text-[10px] sm:text-xs uppercase tracking-wider mt-0.5 sm:mt-1 font-mono truncate" style={{color: 'var(--text-muted)'}}>
              {stat.title}
            </p>
          </div>
        ))}
      </div>

      {/* Best Bets Section */}
      <div className="rounded-xl sm:rounded-2xl overflow-hidden" style={{
        background: `linear-gradient(145deg, var(--bg-dark), var(--secondary-color))`,
        border: '2px solid var(--accent-color)',
        opacity: 0.3,
        boxShadow: '4px 4px 0 rgba(139, 69, 19, 0.2)'
      }} data-testid="best-bets-section">
        <div className="p-3 sm:p-5 border-b-2" style={{borderColor: 'var(--accent-color)', opacity: 0.3, backgroundColor: 'var(--bg-dark)', opacity: 0.5}}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center" style={{background: `linear-gradient(to bottom right, var(--accent-color), var(--primary-color))`, boxShadow: '2px 2px 0 rgba(139, 69, 19, 0.3)'}}>
                <Star className="w-4 h-4 sm:w-5 sm:h-5" style={{color: 'var(--secondary-color)'}} />
              </div>
              <div>
                <h2 className="text-base sm:text-xl font-bold" style={{color: 'var(--text-light)'}}>Best Bets</h2>
                <p className="text-[10px] sm:text-xs font-mono hidden sm:block" style={{color: 'var(--text-muted)'}}>TOP CONTRACTS</p>
              </div>
            </div>
            <Badge className="font-mono text-xs" style={{backgroundColor: 'var(--primary-color)', opacity: 0.2, color: 'var(--primary-color)', borderColor: 'var(--primary-color)'}}>
              {bestBets.length}
            </Badge>
          </div>
        </div>
        
        <div className="divide-y" style={{borderColor: 'var(--primary-color)', opacity: 0.2}}>
          {bestBets.length === 0 ? (
            <div className="p-8 sm:p-12 text-center">
              <Monitor className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4" style={{color: 'var(--primary-color)', opacity: 0.5}} />
              <p className="font-mono text-sm" style={{color: 'var(--text-muted)'}}>NO LEADS YET</p>
            </div>
          ) : (
            bestBets.map((lead, index) => (
              <div 
                key={lead.id}
                className="p-3 sm:p-5 transition-colors cursor-pointer"
                style={{'--hover-bg': 'var(--primary-color)'}}
                onClick={() => setSelectedLead(lead)}
                data-testid={`best-bet-${index}`}
              >
                <div className="flex items-start gap-2 sm:gap-4">
                  {/* Rank Badge */}
                  <span className="w-6 h-6 sm:w-8 sm:h-8 rounded-md sm:rounded-lg flex items-center justify-center font-mono font-bold text-xs sm:text-sm flex-shrink-0" style={{backgroundColor: 'var(--accent-color)', opacity: 0.2, color: 'var(--accent-color)'}}>
                    #{index + 1}
                  </span>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-theme-light font-semibold text-sm sm:text-lg truncate">{lead.title}</h3>
                    <p className="text-xs sm:text-sm text-theme-muted truncate mb-2">{lead.client}</p>
                    
                    {/* Tags - Mobile optimized */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge variant="outline" className="text-[10px] sm:text-xs font-mono px-1.5 py-0">
                        {lead.source.replace('_', ' ').toUpperCase()}
                      </Badge>
                      {lead.clearance && (
                        <Badge variant="outline" className="border-theme-accent/40 text-theme-accent bg-theme-accent/10 text-[10px] sm:text-xs font-mono px-1.5 py-0">
                          {lead.clearance}
                        </Badge>
                      )}
                    </div>
                    
                    {/* Tech Stack - Mobile scrollable */}
                    {lead.tech_stack?.length > 0 && (
                      <div className="flex gap-1 mt-2 overflow-x-auto scrollbar-none -mx-1 px-1">
                        {lead.tech_stack.slice(0, 3).map((tech) => (
                          <span 
                            key={tech}
                            className="px-1.5 py-0.5 text-[10px] sm:text-xs bg-theme-dark text-theme-primary-light rounded font-mono border border-theme-primary/30 flex-shrink-0"
                          >
                            {tech}
                          </span>
                        ))}
                        {lead.tech_stack.length > 3 && (
                          <span className="px-1.5 py-0.5 text-[10px] sm:text-xs text-theme-secondary font-mono flex-shrink-0">
                            +{lead.tech_stack.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Score */}
                  <div className="text-right flex-shrink-0">
                    <div className={`text-xl sm:text-3xl font-bold font-mono ${getScoreColor(lead.score)}`} style={{textShadow: '0 0 10px currentColor'}}>
                      {lead.score}
                    </div>
                    <div className="w-12 sm:w-20 h-1.5 sm:h-2 mt-1 rounded-full bg-theme-darker overflow-hidden">
                      <div 
                        className="h-full rounded-full bg-gradient-to-r from-[#2a9d8f] to-[#40c9ba]"
                        style={{width: `${lead.score}%`}}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Pipeline Summary */}
      {stats?.pipeline_summary && Object.keys(stats.pipeline_summary).length > 0 && (
        <div className="rounded-xl sm:rounded-2xl p-4 sm:p-6" style={{
          background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.8), rgba(38, 70, 83, 0.7))',
          border: '2px solid rgba(42, 157, 143, 0.3)',
          boxShadow: '3px 3px 0 rgba(38, 70, 83, 0.3)'
        }} data-testid="pipeline-summary">
          <h2 className="text-base sm:text-xl font-bold text-theme-light mb-3 sm:mb-4 flex items-center gap-2">
            <KanbanSquare className="w-4 h-4 sm:w-5 sm:h-5 text-theme-secondary" />
            Pipeline
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
            {Object.entries(stats.pipeline_summary).map(([status, count]) => (
              <div 
                key={status}
                className="p-2.5 sm:p-4 rounded-lg bg-theme-darker/60 border border-theme-primary/30"
              >
                <p className="text-[10px] sm:text-xs text-theme-muted uppercase tracking-wider mb-0.5 sm:mb-1 font-mono truncate">
                  {status.replace('_', ' ')}
                </p>
                <p className="text-xl sm:text-2xl font-bold text-theme-light font-mono">{count}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lead Detail Sheet */}
      <LeadDetailSheet 
        lead={selectedLead} 
        open={!!selectedLead} 
        onClose={() => setSelectedLead(null)}
        onUpdate={fetchData}
      />
    </div>
  );
}
